import React, { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, Gamepad2, BarChart3 } from 'lucide-react'
import { useTranslation } from '../hooks/use-i18n'
import { useSelector } from 'react-redux'
import { RootState } from '../store'
import {
  getPublicLeaderboard,
  getPublicLeaderboardCombined,
  getActivePublicTournaments,
  getMyPublicEnrollments,
} from '../services/publicService'
import type { LeaderboardEntry, PublicTournament } from '../types/public'
import type { LeaderboardScope } from '../services/publicService'
import ViewPublicPlayerStatsModal from '../components/ViewPublicPlayerStatsModal'

const PublicLeaderboard: React.FC = () => {
  const { publicTournamentId: urlTournamentId } = useParams<{ publicTournamentId?: string }>()
  const { t } = useTranslation()
  const { user: currentUser } = useSelector((state: RootState) => state.auth)
  const currentUserId = (currentUser as any)?._id ?? (currentUser as any)?.id ?? null

  const [leagues, setLeagues] = useState<PublicTournament[]>([])
  const [selectedLeagueIds, setSelectedLeagueIds] = useState<string[]>([])
  const [entries, setEntries] = useState<LeaderboardEntry[]>([])
  const [scope, setScope] = useState<LeaderboardScope>('world')
  const [loading, setLoading] = useState(true)
  const [scopeParticipant, setScopeParticipant] = useState<{ continentCode?: string; countryCode?: string; city?: string } | null>(null)
  const [showPlayerModal, setShowPlayerModal] = useState(false)
  const [selectedEntry, setSelectedEntry] = useState<LeaderboardEntry | null>(null)
  const [modalViewType, setModalViewType] = useState<'stats' | 'games'>('games')

  // Load football leagues and pre-select from URL
  useEffect(() => {
    let cancelled = false
    getActivePublicTournaments('football').then((list) => {
      if (!cancelled) setLeagues(list)
    }).catch(() => { if (!cancelled) setLeagues([]) })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (urlTournamentId && leagues.some((l) => l.id === urlTournamentId) && selectedLeagueIds.length === 0) {
      setSelectedLeagueIds([urlTournamentId])
    }
  }, [urlTournamentId, leagues, selectedLeagueIds.length])

  // One participant for scope filter (any of my football enrollments)
  useEffect(() => {
    let cancelled = false
    getMyPublicEnrollments().then((enrollments) => {
      if (cancelled) return
      const football = enrollments.filter((e) => leagues.some((l) => l.id === e.publicTournamentId))
      const first = football[0]
      if (first) {
        setScopeParticipant({
          continentCode: first.continentCode,
          countryCode: first.countryCode,
          city: first.city ?? undefined,
        })
      } else {
        setScopeParticipant(null)
      }
    }).catch(() => { if (!cancelled) setScopeParticipant(null) })
    return () => { cancelled = true }
  }, [leagues])

  // Load leaderboard when selection or scope changes
  useEffect(() => {
    if (selectedLeagueIds.length === 0) {
      setEntries([])
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    const filters =
      scope === 'continent' && scopeParticipant?.continentCode
        ? { continentCode: scopeParticipant.continentCode }
        : scope === 'country' && scopeParticipant?.countryCode
          ? { countryCode: scopeParticipant.countryCode }
          : scope === 'city' && scopeParticipant?.city
            ? { city: scopeParticipant.city }
            : undefined
    const load = selectedLeagueIds.length === 1
      ? getPublicLeaderboard(selectedLeagueIds[0], scope, filters)
      : getPublicLeaderboardCombined(selectedLeagueIds, scope, filters)
    load
      .then((list) => {
        if (!cancelled) setEntries(list)
      })
      .catch(() => {
        if (!cancelled) setEntries([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [selectedLeagueIds, scope, scopeParticipant])

  const toggleLeague = (id: string) => {
    setSelectedLeagueIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
  }

  const selectAllLeagues = () => {
    setSelectedLeagueIds(leagues.map((l) => l.id))
  }

  const scopes: { key: LeaderboardScope; label: string }[] = [
    { key: 'world', label: t('publicLeaderboard.world') },
    { key: 'continent', label: t('publicLeaderboard.continent') },
    { key: 'country', label: t('publicLeaderboard.country') },
    { key: 'city', label: t('publicLeaderboard.city') },
  ]

  const title =
    selectedLeagueIds.length === 0
      ? t('publicLeaderboard.personalTitle')
      : selectedLeagueIds.length === 1
        ? `${t('publicLeaderboard.personalTitle')} – ${leagues.find((l) => l.id === selectedLeagueIds[0])?.name ?? ''}`
        : t('publicLeaderboard.titleCombined')

  return (
    <div className="min-h-screen bg-background">
      <main className="pt-16 container mx-auto px-4 pb-8">
        <Link
          to={urlTournamentId ? `/public/${urlTournamentId}` : '/dashboard'}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft size={18} /> {urlTournamentId ? t('publicLeaderboard.backToTournament') : t('dashboard.backToDashboard')}
        </Link>
        <h1 className="font-display text-xl sm:text-2xl font-bold text-foreground mb-2">
          {title}
        </h1>
        <p className="text-sm text-muted-foreground mb-4">
          {t('publicLeaderboard.leagueFilterHint')}
        </p>
        {selectedLeagueIds.length > 1 && (
          <p className="text-xs text-muted-foreground mb-4">
            {t('publicLeaderboard.multiLeagueHint')}
          </p>
        )}
        <div className="flex flex-wrap gap-2 mb-4">
          {leagues.map((league) => (
            <label key={league.id} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-muted/20 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedLeagueIds.includes(league.id)}
                onChange={() => toggleLeague(league.id)}
                className="rounded border-muted"
              />
              <span className="text-sm font-medium text-foreground">{league.name}</span>
            </label>
          ))}
          {leagues.length > 0 && (
            <button
              type="button"
              onClick={selectAllLeagues}
              className="px-3 py-1.5 rounded-xl text-sm font-medium bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground"
            >
              {t('publicLeaderboard.selectAll')}
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2 mb-6">
          {scopes.map((s) => (
            <button
              key={s.key}
              onClick={() => setScope(s.key)}
              className={`px-3 py-1.5 rounded-xl text-sm font-medium ${
                scope === s.key ? 'bg-primary text-primary-foreground' : 'bg-muted/20 text-foreground'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        {selectedLeagueIds.length === 0 ? (
          <p className="text-muted-foreground py-8">{t('publicLeaderboard.selectAtLeastOne')}</p>
        ) : loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
          </div>
        ) : (
          <div className="glass-card rounded-xl overflow-hidden">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-muted/30 bg-muted/10">
                  <th className="p-3 font-semibold text-foreground">#</th>
                  <th className="p-3 font-semibold text-foreground">{t('publicHub.myRank')}</th>
                  <th className="p-3 font-semibold text-foreground">Country</th>
                  <th className="p-3 font-semibold text-foreground">City</th>
                  <th className="p-3 font-semibold text-foreground">Points</th>
                  <th className="p-3 font-semibold text-foreground">{t('publicLeaderboard.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr
                    key={e.userId + (e.publicParticipantId ?? '')}
                    className={`border-b border-muted/20 ${e.userId === currentUserId ? 'bg-primary/10' : ''}`}
                  >
                    <td className="p-3">{e.rank}</td>
                    <td className="p-3 font-medium">{e.userName}</td>
                    <td className="p-3 text-muted-foreground">{e.countryName}</td>
                    <td className="p-3 text-muted-foreground">{e.city ?? '–'}</td>
                    <td className="p-3 font-medium">{e.totalPoints}</td>
                    <td className="p-3">
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedEntry(e)
                            setModalViewType('games')
                            setShowPlayerModal(true)
                          }}
                          className="p-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground text-sm font-medium flex items-center gap-1"
                        >
                          <Gamepad2 size={14} /> {t('roomPage.games')}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedEntry(e)
                            setModalViewType('stats')
                            setShowPlayerModal(true)
                          }}
                          className="p-1.5 rounded-lg bg-chess/10 text-chess hover:bg-chess hover:text-chess-foreground text-sm font-medium flex items-center gap-1"
                        >
                          <BarChart3 size={14} /> {t('roomPage.stats')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {entries.length === 0 && (
              <p className="p-6 text-center text-muted-foreground">{t('publicLeaderboard.noEntries')}</p>
            )}
          </div>
        )}
      </main>
      {selectedEntry && (
        <ViewPublicPlayerStatsModal
          entry={selectedEntry}
          tournamentIds={selectedLeagueIds}
          viewType={modalViewType}
          isOpen={showPlayerModal}
          onClose={() => {
            setShowPlayerModal(false)
            setSelectedEntry(null)
          }}
        />
      )}
    </div>
  )
}

export default PublicLeaderboard
